import { TriStateFilter, CenterFilter } from './types';

const TRI_STATE_FILTERS: TriStateFilter[] = ['all', 'true', 'false'];
const CENTER_FILTERS: CenterFilter[] = ['all', 'cder', 'cber'];

export const isTriStateFilter = (value: unknown): value is TriStateFilter =>
  typeof value === 'string' && TRI_STATE_FILTERS.includes(value as TriStateFilter);

export const isCenterFilter = (value: unknown): value is CenterFilter =>
  typeof value === 'string' && CENTER_FILTERS.includes(value as CenterFilter);

export const parseTriStateFilter = (value: TriStateFilter): boolean | undefined => {
  if (value === 'all') {
    return undefined;
  }

  return value === 'true';
};

export const parseCenterFilter = (value: CenterFilter): string | undefined => {
  if (value === 'all') {
    return undefined;
  }

  return value;
};

export const formatBooleanLabel = (value: boolean): string => (value ? 'Yes' : 'No');

export const formatQcStatusLabel = (value: boolean): string => (value ? 'QCed' : 'Not QCed');

/** Detail-page verdict filter applies to agent `verdict`, not `sme_verdict`. */
export const matchesVerdictFilter = (verdict: boolean | null, filter: TriStateFilter): boolean => {
  if (filter === 'all') {
    return true;
  }

  if (filter === 'true') {
    return verdict === true;
  }

  return verdict === false;
};

export const matchesStringFilter = (value: string | null, filter: string): boolean => {
  if (filter === 'all') {
    return true;
  }

  return (value ?? '').toLowerCase() === filter.toLowerCase();
};

export const matchesIndChangeFilter = (indChange: string | null, filter: string): boolean =>
  matchesStringFilter(indChange, filter);

export const matchesTierFilter = (tier: string | null, filter: string): boolean =>
  matchesStringFilter(tier, filter);

export type ParsedSubmission = {
  submissionType: string;
  submissionNumber: string;
};

export const parseSubmission = (submission: string | null): ParsedSubmission | null => {
  if (!submission) {
    return null;
  }

  // Split on first hyphen only, e.g. "SUPPL-7" → type + number.
  const dashIndex = submission.indexOf('-');
  if (dashIndex <= 0 || dashIndex === submission.length - 1) {
    return null;
  }

  return {
    submissionType: submission.slice(0, dashIndex),
    submissionNumber: submission.slice(dashIndex + 1)
  };
};

export type ComparePdfViewMode = 'full_label' | 'clinical_studies' | 'adverse_reactions';

/** Full label omits the section query param; section tabs pass the mode as-is. */
export const sectionParamFromViewMode = (mode: ComparePdfViewMode): string | undefined =>
  mode === 'full_label' ? undefined : mode;

export type FilterOption = { label: string; value: string };

export const buildDistinctFilterOptions = (
  values: Array<string | null | undefined>
): FilterOption[] => {
  const distinct = new Set<string>();
  values.forEach(value => {
    if (value) {
      distinct.add(value);
    }
  });

  return [
    { label: 'All', value: 'all' },
    ...Array.from(distinct)
      .sort((left, right) => left.localeCompare(right))
      .map(value => ({ label: value, value }))
  ];
};

export const normalizeDocumentId = (value: string | number | null | undefined): number | null => {
  // API may return document IDs as strings; coerce once at fetch time.
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};
